/*
 * SILBERTHAL predictive search (Doofinder-style)
 * Uses Shopify's native Predictive Search API (/search/suggest.json).
 * Markup: snippets/search-autocomplete.liquid inside #the-search-form (sections/header.liquid)
 * Open/close of the form itself stays with assets.js (header.show-search); this file
 * listens to that class and adds the dropdown, keyboard navigation and ARIA state.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 200;
  var CACHE_MAX = 40;
  var LIMITS = { queries: 6, collections: 4, pages: 3, products: 8 };

  var form = document.getElementById('the-search-form');
  var input = document.getElementById('search-input');
  var panel = document.getElementById('sa-panel');
  if (!form || !input || !panel) return;

  var header = form.closest('header') || document.querySelector('header');
  var listbox = panel.querySelector('#sa-listbox');
  var emptyView = panel.querySelector('[data-sa-view="empty"]');
  var resultsView = panel.querySelector('[data-sa-view="results"]');
  var status = panel.querySelector('[data-sa-status]');
  var closeBtn = document.getElementById('close-search');
  var searchIcon = document.getElementById('search-icon');

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var suggestUrl = (panel.getAttribute('data-suggest-url') || root + 'search/suggest') + '.json';
  var searchUrl = panel.getAttribute('data-search-url') || root + 'search';
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  var moneyFmt;
  try {
    moneyFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency });
  } catch (e) {
    moneyFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  }

  var cache = new Map();
  var controller = null;
  var timer = null;
  var lastQuery = '';
  var activeIndex = -1;
  var isOpen = false;

  /* ---------- helpers ---------- */

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escRe(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Escapes text and wraps every typed word in <mark>
  function highlight(text, query) {
    text = String(text == null ? '' : text);
    var words = query.split(/\s+/).filter(function (w) { return w.length > 0; });
    if (!words.length) return esc(text);
    words.sort(function (a, b) { return b.length - a.length; });
    var re = new RegExp('(' + words.map(escRe).join('|') + ')', 'gi');
    return text.split(re).map(function (part, i) {
      return i % 2 ? '<mark>' + esc(part) + '</mark>' : esc(part);
    }).join('');
  }

  function money(value) {
    var n = parseFloat(value);
    return isNaN(n) ? '' : moneyFmt.format(n);
  }

  function imgUrl(url, width) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function fullSearchUrl(q) {
    return searchUrl + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last';
  }

  function withType(url) {
    if (!url) return '#';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'type=product&options%5Bprefix%5D=last';
  }

  var ICON_SEARCH = '<svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>';
  var ICON_GRID = '<svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>';
  var ICON_DOC = '<svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="14 3 14 9 20 9"/></svg>';
  var ICON_ARROW = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';

  /* ---------- open / close ---------- */

  // Bottom edge of the open search bar in the viewport (ignores the slide-in transform)
  function setTopOffset() {
    var parent = form.offsetParent || header;
    var top = (parent ? parent.getBoundingClientRect().top : 0) + form.offsetTop + form.offsetHeight;
    panel.style.setProperty('--sa-top', Math.max(0, Math.round(top)) + 'px');
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    setTopOffset();
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('sa-open');
    render(input.value.trim());
  }

  function close(opts) {
    if (!isOpen) return;
    isOpen = false;
    if (controller) controller.abort();
    clearTimeout(timer);
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    document.documentElement.classList.remove('sa-open');
    activeIndex = -1;
    // assets.js toggles show-search on every <header> and only re-opens when none has it,
    // so clear it everywhere or the search icon stops working.
    if (!(opts && opts.keepForm)) {
      Array.prototype.forEach.call(document.querySelectorAll('header.show-search'), function (el) {
        el.classList.remove('show-search');
      });
    }
  }

  /* ---------- fetching ---------- */

  function fetchSuggestions(q) {
    var key = q.toLowerCase();
    if (cache.has(key)) return Promise.resolve(cache.get(key));

    if (controller) controller.abort();
    controller = new AbortController();

    var params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', 'product,collection,page,article,query');
    params.set('resources[limit]', '8');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.sku,variants.title,tag,vendor');

    return fetch(suggestUrl + '?' + params.toString(), {
      signal: controller.signal,
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var r = (data && data.resources && data.resources.results) || {};
        if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
        cache.set(key, r);
        return r;
      });
  }

  /* ---------- rendering ---------- */

  function showView(name) {
    emptyView.hidden = name !== 'empty';
    resultsView.hidden = name !== 'results';
    activeIndex = -1;
    input.removeAttribute('aria-activedescendant');
    panel.classList.toggle('is-empty-state', name === 'empty');
  }

  function render(q) {
    lastQuery = q;
    if (q.length < MIN_CHARS) {
      if (controller) controller.abort();
      panel.classList.remove('is-loading');
      showView('empty');
      status.textContent = '';
      return;
    }
    var key = q.toLowerCase();
    if (cache.has(key)) {
      paint(q, cache.get(key));
      return;
    }
    // Skeleton only if nothing is shown yet, otherwise keep old results dimmed
    if (resultsView.hidden || !resultsView.firstChild) {
      resultsView.innerHTML = skeletonHtml();
      showView('results');
    }
    panel.classList.add('is-loading');
    listbox.setAttribute('aria-busy', 'true');
    fetchSuggestions(q)
      .then(function (r) {
        if (q !== lastQuery) return;
        paint(q, r);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (q !== lastQuery) return;
        panel.classList.remove('is-loading');
        listbox.removeAttribute('aria-busy');
        resultsView.innerHTML =
          '<div class="sa-noresults"><p class="sa-noresults__head">Die Vorschau lädt gerade nicht.</p>' +
          '<p>Drück Enter, dann suchen wir ganz normal nach „' + esc(q) + '“.</p></div>';
        showView('results');
      });
  }

  function skeletonHtml() {
    var cards = '';
    for (var i = 0; i < 8; i++) {
      cards += '<div class="sa-product sa-skel"><span class="sa-product__img"></span><span class="sa-product__body"><span class="sa-skel__line"></span><span class="sa-skel__line is-short"></span></span></div>';
    }
    return '<div class="sa-col sa-col--side"><div class="sa-group"><span class="sa-skel__line is-title"></span><span class="sa-skel__line"></span><span class="sa-skel__line"></span><span class="sa-skel__line is-short"></span></div></div>' +
      '<div class="sa-col sa-col--main"><div class="sa-group"><span class="sa-skel__line is-title"></span><div class="sa-products">' + cards + '</div></div></div>';
  }

  function productHtml(p, q, i) {
    var img = (p.featured_image && p.featured_image.url) || p.image || '';
    var alt = '';
    var price = parseFloat(p.price_min || p.price);
    var compare = parseFloat(p.compare_at_price_max || p.compare_at_price_min || 0);
    var onSale = compare > price;
    var varies = p.price_max && parseFloat(p.price_max) > parseFloat(p.price_min);
    var html = '<a class="sa-product sa-opt' + (p.available === false ? ' is-soldout' : '') + '" role="option" id="sa-p-' + i + '" href="' + esc(p.url) + '">';
    html += '<span class="sa-product__img">';
    if (img) {
      html += '<img src="' + esc(imgUrl(img, 160)) + '" srcset="' + esc(imgUrl(img, 160)) + ' 1x, ' + esc(imgUrl(img, 320)) + ' 2x" width="160" height="160" alt="' + esc(alt) + '" loading="lazy" decoding="async">';
    }
    if (onSale) html += '<span class="sa-badge">Sale</span>';
    html += '</span><span class="sa-product__body">';
    if (p.type) html += '<span class="sa-product__type">' + esc(p.type) + '</span>';
    html += '<span class="sa-product__title">' + highlight(p.title, q) + '</span>';
    html += '<span class="sa-price">' + (varies ? '<span class="sa-price__from">ab</span> ' : '') +
      '<span class="sa-price__now' + (onSale ? ' is-sale' : '') + '">' + money(price) + '</span>' +
      (onSale ? '<s class="sa-price__was">' + money(compare) + '</s>' : '') + '</span>';
    if (p.available === false) html += '<span class="sa-product__stock">Gerade ausverkauft</span>';
    html += '</span></a>';
    return html;
  }

  function linkListHtml(items, prefix, icon, q, urlFn) {
    return '<ul class="sa-links">' + items.map(function (it, i) {
      var label = it.text || it.title;
      return '<li><a class="sa-link sa-opt" role="option" id="' + prefix + i + '" href="' + esc(urlFn ? urlFn(it.url) : it.url) + '">' +
        icon + '<span>' + highlight(label, q) + '</span></a></li>';
    }).join('') + '</ul>';
  }

  function group(id, title, inner) {
    return '<div class="sa-group sa-group--' + id + '" role="group" aria-labelledby="sa-h-' + id + '"><p class="sa-title" id="sa-h-' + id + '">' + title + '</p>' + inner + '</div>';
  }

  function paint(q, r) {
    panel.classList.remove('is-loading');
    listbox.removeAttribute('aria-busy');

    var queries = (r.queries || []).filter(function (s) {
      return s.text && s.text.toLowerCase() !== q.toLowerCase();
    }).slice(0, LIMITS.queries);
    var collections = (r.collections || []).slice(0, LIMITS.collections);
    var pages = (r.pages || []).concat(r.articles || []).slice(0, LIMITS.pages);
    var products = (r.products || []).slice(0, LIMITS.products);

    if (!products.length && !collections.length && !pages.length && !queries.length) {
      resultsView.innerHTML = noResultsHtml(q);
      showView('results');
      status.textContent = 'Keine Treffer für ' + q;
      return;
    }

    var side = '';
    if (queries.length) side += group('q', 'Suchvorschläge', linkListHtml(queries, 'sa-q-', ICON_SEARCH, q, withType));
    if (collections.length) side += group('c', 'Kategorien', linkListHtml(collections, 'sa-c-', ICON_GRID, q));
    if (pages.length) side += group('pg', 'Ratgeber', linkListHtml(pages, 'sa-pg-', ICON_DOC, q));

    var main = '';
    if (products.length) {
      main = group('p', 'Produkte', '<div class="sa-products">' + products.map(function (p, i) {
        return productHtml(p, q, i);
      }).join('') + '</div>');
    } else {
      main = '<div class="sa-noresults sa-noresults--inline"><p class="sa-noresults__head">Keine passenden Produkte</p><p>Schau mal bei den Vorschlägen links, da ist vielleicht das Richtige dabei.</p></div>';
    }

    resultsView.innerHTML =
      (side ? '<div class="sa-col sa-col--side">' + side + '</div>' : '') +
      '<div class="sa-col sa-col--main">' + main + '</div>' +
      '<div class="sa-footer"><a class="sa-all sa-opt" role="option" id="sa-all" href="' + esc(fullSearchUrl(q)) + '">' +
      'Alle Ergebnisse für „' + esc(q) + '“ anzeigen' + ICON_ARROW + '</a></div>';
    showView('results');
    status.textContent = products.length + (products.length === 1 ? ' Produkt' : ' Produkte') + ' gefunden';
  }

  function noResultsHtml(q) {
    var chips = emptyView.querySelector('.sa-chips');
    var featured = emptyView.querySelector('.sa-col--main');
    var html = '<div class="sa-col sa-col--side"><div class="sa-noresults">' +
      '<p class="sa-noresults__head">Zu „' + esc(q) + '“ haben wir nichts gefunden.</p>' +
      '<p>Prüf die Schreibweise oder probier einen allgemeineren Begriff.</p></div>';
    if (chips) {
      html += '<div class="sa-group" role="group" aria-labelledby="sa-h-nr"><p class="sa-title" id="sa-h-nr">Beliebte Suchen</p>' +
        chips.outerHTML.replace(/id="sa-pop-/g, 'id="sa-nr-pop-') + '</div>';
    }
    html += '</div>';
    if (featured) {
      html += featured.outerHTML.replace(/id="sa-feat-/g, 'id="sa-nr-feat-').replace(/id="sa-h-featured"/, 'id="sa-h-nr-featured"').replace(/aria-labelledby="sa-h-featured"/, 'aria-labelledby="sa-h-nr-featured"');
    }
    return html;
  }

  /* ---------- keyboard ---------- */

  function options() {
    var view = resultsView.hidden ? emptyView : resultsView;
    return Array.prototype.slice.call(view.querySelectorAll('.sa-opt')).filter(function (el) {
      return el.getClientRects().length > 0;
    });
  }

  function setActive(idx) {
    var opts = options();
    opts.forEach(function (o) {
      o.classList.remove('is-active');
      o.setAttribute('aria-selected', 'false');
    });
    if (!opts.length) {
      activeIndex = -1;
      return;
    }
    if (idx < -1) idx = opts.length - 1;
    if (idx >= opts.length) idx = -1;
    activeIndex = idx;
    if (idx === -1) {
      input.removeAttribute('aria-activedescendant');
      return;
    }
    var el = opts[idx];
    el.classList.add('is-active');
    el.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', el.id);
    el.scrollIntoView({ block: 'nearest' });
  }

  input.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (input.value) {
        input.value = '';
        render('');
      } else {
        close();
        if (searchIcon) searchIcon.focus();
      }
      return;
    }
    if (!isOpen) {
      if (e.key === 'ArrowDown') open();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(activeIndex - 1);
    } else if (e.key === 'Enter') {
      var opts = options();
      var el = activeIndex > -1 ? opts[activeIndex] : null;
      if (el) {
        e.preventDefault();
        if (el.hasAttribute('data-term')) {
          useTerm(el.getAttribute('data-term'));
        } else {
          window.location.href = el.href;
        }
      }
    }
  });

  /* ---------- events ---------- */

  function useTerm(term) {
    input.value = term;
    input.focus();
    render(term);
  }

  input.addEventListener('input', function () {
    if (!isOpen) open();
    var q = input.value.trim();
    clearTimeout(timer);
    if (q.length < MIN_CHARS) {
      render(q);
      return;
    }
    var key = q.toLowerCase();
    if (cache.has(key)) {
      render(q);
      return;
    }
    timer = setTimeout(function () { render(q); }, DEBOUNCE_MS);
  });

  input.addEventListener('focus', function () {
    if (header && header.classList.contains('show-search')) open();
  });

  // Popular chips fill the input instead of leaving the page
  panel.addEventListener('click', function (e) {
    var chip = e.target.closest('[data-term]');
    if (chip) {
      e.preventDefault();
      useTerm(chip.getAttribute('data-term'));
    }
  });

  // Hover syncs the keyboard highlight
  panel.addEventListener('mousemove', function (e) {
    var opt = e.target.closest('.sa-opt');
    if (!opt || opt.classList.contains('is-active')) return;
    var idx = options().indexOf(opt);
    if (idx > -1) {
      options().forEach(function (o) { o.classList.remove('is-active'); o.setAttribute('aria-selected', 'false'); });
      activeIndex = idx;
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', opt.id);
    }
  });

  // Open search: assets.js toggles header.show-search; we focus right in the click
  // so iOS brings up the keyboard.
  if (searchIcon) {
    searchIcon.addEventListener('click', function () {
      input.focus({ preventScroll: true });
      open();
    });
  }
  if (closeBtn) {
    closeBtn.addEventListener('click', function () { close({ keepForm: true }); });
  }

  if (header && 'MutationObserver' in window) {
    new MutationObserver(function () {
      if (!header.classList.contains('show-search')) close({ keepForm: true });
    }).observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('click', function (e) {
    if (!isOpen) return;
    if (form.contains(e.target) || (searchIcon && searchIcon.contains(e.target))) return;
    close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen && document.activeElement !== input) close();
  });

  window.addEventListener('resize', function () {
    if (isOpen) setTopOffset();
  });
})();
